import React, { useEffect, useState } from 'react';
import api from '../../lib/axios';
import { useActionBar } from '../../contexts/ActionBarContext';
import { SendEmailDialog, SendSmsDialog, SendAppDialog, PrintLetterDialog } from './ActionBarDialogs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Printer, Mail, Mails, MessageSquareText, AtSign, Euro, Plus, Loader2, Smartphone, Mic, Phone, PhoneOff } from 'lucide-react';
import { incomingCallsEnabled, setIncomingCallsEnabled, INCOMING_CALLS_EVENT } from '@/lib/incomingCalls';
import { formatDeNumber } from '@/lib/utils';
import { toast } from 'sonner';
import { requestVoiceStart } from '@/lib/voiceControl';

function BarButton({ icon: Icon, label, onClick, disabled, testId, dark, accent, listening }) {
  const cls = dark
    ? (disabled
        ? 'text-white/30 cursor-not-allowed'
        : listening
          ? 'text-emerald-200 bg-white/10 hover:bg-white/20'
          : accent
            ? 'bg-brand-red text-white hover:bg-brand-red/90'
            : 'text-white/90 hover:bg-white/10')
    : (disabled
        ? 'text-slate-300 cursor-not-allowed'
        : listening
          ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
          : accent
            ? 'bg-brand-red text-white hover:bg-brand-red/90'
            : 'text-brand-muted hover:text-brand-anthracite hover:bg-brand-canvas');
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`p-2 rounded-md transition-colors ${cls}`}
      data-testid={testId}
    >
      <Icon className="h-[18px] w-[18px]" />
    </button>
  );
}

export function ActionBar({ variant = 'light', inline = false }) {
  const { config, pendingAction, clearPendingAction } = useActionBar();
  const { selected, list, onNew, newLabel, newMenu } = config || {};
  const [dialog, setDialog] = useState(null);
  const [invoiceConfirm, setInvoiceConfirm] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [callsOn, setCallsOn] = useState(incomingCallsEnabled);

  useEffect(() => {
    const onToggle = (event) => setCallsOn(!!event.detail);
    window.addEventListener(INCOMING_CALLS_EVENT, onToggle);
    return () => window.removeEventListener(INCOMING_CALLS_EVENT, onToggle);
  }, []);

  const hasSelected = !!selected;
  const hasList = (list?.ids?.length || 0) > 0;
  const isGuardian = selected?.kind === 'guardian';
  const dark = variant === 'dark';
  const hasNewMenu = Array.isArray(newMenu) && newMenu.length > 0;
  const canCreate = !!onNew || hasNewMenu;

  useEffect(() => {
    if (!pendingAction) return;
    const action = pendingAction;
    clearPendingAction();
    if (action === 'invoice') {
      if (!isGuardian) {
        toast.error('Rechnung: bitte zuerst einen Zahler auswählen');
        return;
      }
      setInvoiceConfirm(true);
      return;
    }
    if (action === 'print' || action === 'sms' || action === 'app') {
      if (!hasSelected && !hasList) {
        toast.error('Bitte zuerst einen Datensatz auswählen');
        return;
      }
      setDialog(action);
      return;
    }
    if (action === 'mail-single' || action === 'mail-free') {
      if (!hasSelected) {
        toast.error('Bitte zuerst einen Datensatz auswählen');
        return;
      }
      setDialog(action);
      return;
    }
    if (action === 'mail-bulk') {
      if (!hasList) {
        toast.error('Keine Datensätze in der Liste');
        return;
      }
      setDialog(action);
    }
  }, [pendingAction, clearPendingAction, hasSelected, hasList, isGuardian]);

  if (!inline && config?.ownBar) return null;

  const createInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const r = await api.post(`/guardians/${selected.id}/create-invoice`, {});
      if (r.data.invoices_created > 0) {
        toast.success(`${r.data.invoices_created} Rechnung(en) für ${r.data.guardian_name} erstellt (Summe ${formatDeNumber(r.data.total_amount || 0)} €)`);
      } else if (r.data.skipped_reasons?.already_invoiced) {
        toast.info('Für diesen Monat existiert bereits eine Rechnung für diese Familie');
      } else {
        toast.info('Keine abrechenbaren Positionen gefunden');
      }
      setInvoiceConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rechnung konnte nicht erstellt werden');
    } finally { setInvoiceLoading(false); }
  };

  return (
    <>
      <div className="flex items-center gap-0.5" data-testid="global-action-bar">
        <BarButton dark={dark} icon={Printer} testId="actionbar-print-btn"
          label={hasSelected || hasList ? 'Druckmenü: Brief / Serienbrief aus Vorlage' : 'Druckmenü (zuerst Datensatz auswählen)'}
          disabled={!hasSelected && !hasList} onClick={() => setDialog('print')} />
        <BarButton dark={dark} icon={Mail} testId="actionbar-single-mail-btn"
          label={hasSelected ? `Einzelmail aus Korrespondenz an ${selected.name}` : 'Einzelmail (zuerst Datensatz auswählen)'}
          disabled={!hasSelected} onClick={() => setDialog('mail-single')} />
        <BarButton dark={dark} icon={Mails} testId="actionbar-bulk-mail-btn"
          label={hasList ? `Sammelmail an alle aufgerufenen Datensätze (${list.ids.length})` : 'Sammelmail (keine Datensätze aufgerufen)'}
          disabled={!hasList} onClick={() => setDialog('mail-bulk')} />
        <div className={`w-px h-5 mx-1 ${dark ? 'bg-white/20' : 'bg-slate-200'}`} />
        <BarButton dark={dark} icon={MessageSquareText} testId="actionbar-sms-btn"
          label={hasSelected || hasList ? 'SMS versenden (Freitext mit Platzhaltern)' : 'SMS (zuerst Datensatz auswählen)'}
          disabled={!hasSelected && !hasList} onClick={() => setDialog('sms')} />
        <BarButton dark={dark} icon={Smartphone} testId="actionbar-app-btn"
          label={hasSelected || hasList ? 'App-Nachricht versenden (Inbox und Push)' : 'App-Nachricht (zuerst Datensatz auswählen)'}
          disabled={!hasSelected && !hasList} onClick={() => setDialog('app')} />
        <BarButton dark={dark} icon={AtSign} testId="actionbar-freetext-mail-btn"
          label={hasSelected ? `Mailversand (Freitext) an ${selected.name}` : 'Mailversand (zuerst Datensatz auswählen)'}
          disabled={!hasSelected} onClick={() => setDialog('mail-free')} />
        <div className={`w-px h-5 mx-1 ${dark ? 'bg-white/20' : 'bg-slate-200'}`} />
        <BarButton dark={dark} icon={Euro} testId="actionbar-invoice-btn"
          label={isGuardian ? `Rechnung erstellen für ${selected.name}` : 'Rechnung erstellen (zuerst Zahler auswählen)'}
          disabled={!isGuardian} onClick={() => setInvoiceConfirm(true)} />
        <div className={`w-px h-5 mx-1 ${dark ? 'bg-white/20' : 'bg-slate-200'}`} />
        <BarButton dark={dark} icon={callsOn ? Phone : PhoneOff} listening={callsOn}
          testId="actionbar-incoming-calls-btn"
          label={callsOn
            ? 'Eingehende Anrufe werden angezeigt — klicken zum Ausschalten'
            : 'Eingehende Anrufe sind aus — klicken zum Einschalten'}
          onClick={() => {
            setIncomingCallsEnabled(!callsOn).catch(() => {
              toast.error('Die Einstellung konnte nicht gespeichert werden');
            });
          }} />
        <BarButton dark={dark} icon={Mic} testId="actionbar-voice-btn"
          label="Sprachsteuerung (F8). Nochmal oder Aus beendet sie."
          onClick={() => requestVoiceStart()} />
        {hasNewMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                title={newLabel || 'Neu anlegen'}
                className={`p-2 rounded-md transition-colors ${
                  dark
                    ? 'bg-brand-red text-white hover:bg-brand-red/90'
                    : 'bg-brand-red text-white hover:bg-brand-red/90'
                }`}
                data-testid="actionbar-new-btn"
              >
                <Plus className="h-[18px] w-[18px]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72" data-testid="actionbar-new-menu">
              <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
                Was möchten Sie anlegen?
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {newMenu.map((item) => (
                <DropdownMenuItem
                  key={item.id || item.label}
                  className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
                  onSelect={() => item.onClick && item.onClick()}
                  data-testid={item.testId || `actionbar-new-${item.id}`}
                >
                  <span className="font-medium text-slate-900">{item.label}</span>
                  {item.description ? (
                    <span className="text-xs text-slate-500 leading-snug whitespace-normal">
                      {item.description}
                    </span>
                  ) : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <BarButton dark={dark} accent icon={Plus} testId="actionbar-new-btn"
            label={onNew ? (newLabel || 'Neuen Datensatz anlegen') : 'Neu (auf dieser Seite nicht verfügbar)'}
            disabled={!canCreate} onClick={() => onNew && onNew()} />
        )}
      </div>

      <SendEmailDialog open={dialog === 'mail-single'} onClose={() => setDialog(null)} mode="single" freeText={false} selected={selected} list={list} />
      <SendEmailDialog open={dialog === 'mail-bulk'} onClose={() => setDialog(null)} mode="bulk" freeText={false} selected={selected} list={list} />
      <SendEmailDialog open={dialog === 'mail-free'} onClose={() => setDialog(null)} mode="single" freeText={true} selected={selected} list={list} />
      <SendSmsDialog open={dialog === 'sms'} onClose={() => setDialog(null)} selected={selected} list={list} />
      <SendAppDialog open={dialog === 'app'} onClose={() => setDialog(null)} selected={selected} list={list} />
      <PrintLetterDialog open={dialog === 'print'} onClose={() => setDialog(null)} selected={selected} list={list} />

      <Dialog open={invoiceConfirm} onOpenChange={setInvoiceConfirm}>
        <DialogContent className="max-w-md" data-testid="actionbar-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Rechnung erstellen</DialogTitle>
            <DialogDescription>
              Monatsrechnung für die Familie von <span className="font-medium text-slate-700">{selected?.name}</span> aus
              der aktuellen Beitragsberechnung erzeugen? Existiert bereits eine Rechnung für diesen Monat, wird nichts doppelt angelegt.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInvoiceConfirm(false)} data-testid="actionbar-invoice-cancel">Abbrechen</Button>
            <Button onClick={createInvoice} disabled={invoiceLoading} data-testid="actionbar-invoice-confirm">
              {invoiceLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Euro className="h-4 w-4 mr-1.5" />}
              Rechnung erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
